import { Component, DestroyRef, Input, OnInit, forwardRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { portalConst } from '@app/config';
import { LearningTrailsElementsService } from '@app/core';
import { StatusEnum } from '@app/core/models/status.enum';
import { debounceDelay, notNull } from '@app/shared/utils';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { BehaviorSubject, filter, switchMap, takeWhile, timer } from 'rxjs';

@Component({
  selector: 'app-upload-video-element',
  templateUrl: './upload-video-element.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => UploadVideoElementComponent),
    },
  ],
  standalone: false,
})
export class UploadVideoElementComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly learningTrailsElementsService = inject(LearningTrailsElementsService);
  private readonly destroyRef = inject(DestroyRef);
  public submittedForm = false;
  public uploading = false;
  public uploaded = false;
  public form: FormGroup;
  public preview_url$ = new BehaviorSubject<string>(undefined);
  public rendering$ = new BehaviorSubject<boolean>(false);

  public config: DropzoneConfigInterface = {
    clickable: true,
    url: `${portalConst.api.vimeo}/v1/upload-video`,
    params: {
      conta: portalConst.vimeo_account,
      origem: 'trilhas-de-aprendizagem',
      verificaTamanho: true,
      pesoMaximo: 10000,
    },
    createImageThumbnails: false,
    maxFiles: 1,
    maxFilesize: 10000,
    acceptedFiles: '.mp4,.mov,.m4v',
    timeout: 270000000,
  };

  @Input()
  public set submitted(value: boolean) {
    this.submittedForm = value;
    if (value) {
      this.form.markAllAsTouched();
    }
  }

  public ngOnInit(): void {
    this.initForm();
  }

  public onUpload(data: any): void {
    this.uploaded = true;
    this.preview_url$.next(null);
    this.form.reset();
    this.form.patchValue(data);
    this.form.get('hash').setValue('hash');
    this.form.get('file').reset();
    this.form.get('video').setValue(data.file);
    this.form.get('nome_arquivo').setValue(data.title);
    this.form.get('extensao').setValue(data.extension);
    this.form.get('vimeo_conta').setValue(portalConst.vimeo_account);
    this.setValidators(this.form.value);
    this.onSend();
    this.getInfoVimeo();
  }

  public onDelete(): void {
    this.form.reset();
    this.setValidators(this.form.value);
    this.onSend();
    this.form.get('vimeo_conta').setValue(portalConst.vimeo_account);
    this.rendering$.next(false);
    this.preview_url$.next(undefined);
    this.uploaded = false;
    this.uploading = false;
  }

  public onProcessingVideo(): void {
    this.uploading = true;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public writeValue(data: any): void {
    if (!!data) {
      if (!!data?.video) {
        if (!!data?.vimeo_hash) {
          this.preview_url$.next(`${portalConst.vimeo_url}${data.video.split('/')[2]}?h=${data.vimeo_hash}`);
        } else {
          this.getInfoVimeo(data.id, data.video);
        }
      } else if (!!data?.link && data.link.includes('player.vimeo.com/video/') && !data.link.includes('&byline=0')) {
        data.link = data.link.concat('&title=0&portrait=0&byline=0');
      }
      this.form.patchValue(data);
      this.setValidators(this.form.value);
    }
  }

  private onSend(): void {
    this.onTouched();
    if (this.form.valid) {
      this.onChange(this.form.value);
    } else {
      this.onChange(undefined);
    }
  }

  private initForm(): void {
    const regexUrl =
      /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;
    this.form = this.formBuilder.group({
      id: undefined,
      hash: undefined,
      link: [undefined, [Validators.required, Validators.pattern(regexUrl), Validators.maxLength(255)]],
      nome_arquivo: [undefined, [Validators.required]],
      video: [undefined, [Validators.required]],
      vimeo_conta: [portalConst.vimeo_account, [Validators.required]],
      file: undefined,
      vimeo_hash: undefined,
      thumbnail: undefined,
      extensao: [undefined, [Validators.required]],
      mimetype: undefined,
      size: 0,
    });

    this.changLink();
  }

  private getInfoVimeo(videoId: number = null, vimeo: string = null): void {
    const data = {
      id: this.form.value.id ?? videoId,
      video: this.form.value.video ?? vimeo,
      conta: this.form.value.vimeo_conta,
    };
    this.rendering$.next(true);
    timer(0, 60000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeWhile(() => this.rendering$.value),
        switchMap(() => this.learningTrailsElementsService.checkVideo(data)),
        filter(({ ret }) => ret === StatusEnum.Active),
      )
      .subscribe((video: any) => {
        this.form.get('thumbnail').setValue(video.thumbnail);
        this.form.get('vimeo_hash').setValue(video.vimeo_hash);
        this.preview_url$.next(
          `https://player.vimeo.com/video/${this.form.value.video.split('/')[2]}?h=${video.vimeo_hash}`,
        );
        this.onSend();
        this.rendering$.next(false);
      });
  }

  private changLink(): void {
    this.form.controls.link.valueChanges.pipe(notNull(), debounceDelay()).subscribe((link) => {
      if (link === this.preview_url$.value) return;
      this.preview_url$.next(null);
      if (link.includes('vimeo.com')) {
        this.validateVimeo(link);
      } else if (link.includes('youtube.com') || link.includes('youtu.be')) {
        this.validateYoutube(link);
      } else {
        this.preview_url$.next(null);
        this.form.controls.link.setErrors({ mask: true });
      }
      this.setValidators({ ...this.form.value, link });
      if (!this.form.value.hash) {
        this.form.get('hash').setValue('hash');
      }
      this.onSend();
    });
  }

  private setValidators(value: any) {
    if (!!value.link) {
      this.form.controls.nome_arquivo.clearValidators();
      this.form.controls.nome_arquivo.updateValueAndValidity();
      this.form.controls.video.clearValidators();
      this.form.controls.video.updateValueAndValidity();
      this.form.controls.extensao.clearValidators();
      this.form.controls.extensao.updateValueAndValidity();
      this.form.controls.vimeo_conta.clearValidators();
      this.form.controls.vimeo_conta.updateValueAndValidity();
      this.form.controls.link.addValidators(Validators.required);
      this.form.controls.link.updateValueAndValidity();
    } else if (!!value.video) {
      this.form.controls.nome_arquivo.addValidators(Validators.required);
      this.form.controls.nome_arquivo.updateValueAndValidity();
      this.form.controls.video.addValidators(Validators.required);
      this.form.controls.video.updateValueAndValidity();
      this.form.controls.extensao.addValidators(Validators.required);
      this.form.controls.extensao.updateValueAndValidity();
      this.form.controls.vimeo_conta.addValidators(Validators.required);
      this.form.controls.vimeo_conta.updateValueAndValidity();
      this.form.controls.link.removeValidators(Validators.required);
      this.form.controls.link.updateValueAndValidity();
    } else {
      this.form.controls.nome_arquivo.addValidators(Validators.required);
      this.form.controls.nome_arquivo.updateValueAndValidity();
      this.form.controls.video.addValidators(Validators.required);
      this.form.controls.video.updateValueAndValidity();
      this.form.controls.extensao.addValidators(Validators.required);
      this.form.controls.extensao.updateValueAndValidity();
      this.form.controls.vimeo_conta.addValidators(Validators.required);
      this.form.controls.vimeo_conta.updateValueAndValidity();
      this.form.controls.link.addValidators(Validators.required);
      this.form.controls.link.updateValueAndValidity();
    }
  }

  private validateVimeo(link: string): void {
    if (link.includes('video/')) {
      const split = link.split('video/');
      const url = link.concat(split[1].toString());
      this.form.controls.link.setValue(url, { emitEvent: false });
      this.preview_url$.next(url);
    } else if (link.includes('channels/')) {
      // Videos de canais do Vimeo
      const split = link.split('channels/');
      const split2 = split[1].split('/');
      const url = link.concat(split2[1].toString());
      this.form.get('link').setValue(url, { emitEvent: false });
      this.preview_url$.next(url);
    } else if (link.includes('groups/')) {
      // Videos de grupos do Vimeo
      const split = link.split('videos/');
      const url = link.concat(split[1].toString());
      this.form.controls.link.setValue(url, { emitEvent: false });
      this.preview_url$.next(link.concat(url));
    } else if (link.includes('ondemand/')) {
      // Videos sob demanda do Vimeo
      const split = link.split('ondemand/');
      const split2 = split[1].split('/');
      const url = link.concat(split2[1].toString());
      this.form.controls.link.setValue(url, { emitEvent: false });
      this.preview_url$.next(link.concat(url));
    } else {
      const split = link.split('/');
      if (!!split[4]) {
        const url = `${portalConst.vimeo_url}${split[3]}?h=${split[4].replace('?', '&')}&title=0&portrait=0&byline=0`;
        this.form.controls.link.setValue(url, { emitEvent: false });
        this.preview_url$.next(url);
      } else {
        this.preview_url$.next(null);
        this.form.controls.link.setErrors({ mask: true });
      }
    }
  }

  private validateYoutube(link: string): void {
    if (link.includes('youtube.com')) {
      const initLink = 'https://www.youtube.com/embed/';
      if (link.includes('watch?v=')) {
        const split = link.split('v=')[1].split('&');
        const url = initLink + split[0];
        this.form.controls.link.setValue(url, { emitEvent: false });
        this.preview_url$.next(url);
      } else if (link.includes('live/')) {
        // Lives do Youtube
        const split = link.split('live/');
        const url = initLink + split[1];
        this.form.controls.link.setValue(url, { emitEvent: false });
        this.preview_url$.next(url);
      } else if (link.includes('embed/')) {
        // Link de Retorno
        const split = link.split('embed/');
        const url = initLink + split[1];
        this.form.controls.link.setValue(url, { emitEvent: false });
        this.preview_url$.next(url);
      } else {
        this.preview_url$.next(null);
        this.form.controls.link.setErrors({ mask: true });
      }
    } else if (link.includes('youtu.be')) {
      // Link de compartilhamento do Youtube
      const split = link.split('.be/');
      const url = `https://www.youtube.com/embed/${split[1]}`;
      this.form.controls.link.setValue(url, { emitEvent: false });
      this.preview_url$.next(url);
    }
  }

  public onTouched = () => true;
  private onChange = (_: any) => true;
}
